/**
 * DrugsServiceModule — 2026-06-08 Phase 4D wire-up for the canonical
 * Q Manual VG35002 drugs + poisons catalog REST surface.
 *
 * Wraps the briefing-room backend's drugs-library endpoints (Phase 4C,
 * commit 3cc221783). Lets the SPA list every drug + poison with compact-
 * shape metadata and fetch a single entry's full data.
 *
 * Routes (per briefing_room_backend/routes/drugs.py):
 *   GET /api/modules/briefing-room/drugs           → DrugsManifest
 *   GET /api/modules/briefing-room/drugs/{drug_id} → full drug JSON | 404
 *
 * Schema-quirk: source seed file `drugs.json` uses entries-array key
 * `agents` (NOT `drugs`). The backend manifest surfaces `top_level_key`
 * metadata so SPA consumers can verify the file shape without reading
 * the JSON directly.
 *
 * Auth: Bearer JWT from localStorage via getAuthHeaders().
 */
import { getAuthHeaders } from './authHeaders';

export type DrugCategory = 'drug' | 'poison';

export interface DrugManifestEntry {
  id: string;
  name: string;
  category: DrugCategory;
  manifest_section: string | null;
  page: number | null;
}

export interface DrugsManifest {
  drugs: DrugManifestEntry[];
  total: number;
  schema_version: string;
  source: string;
  /**
   * Always 'agents' for the drugs.json file (file name does not match
   * entries-array key; per F5 audit metadata).
   */
  top_level_key: string | null;
}

const BASE_PATH = '/api/modules/briefing-room/drugs';

export class DrugsServiceModule {
  constructor(
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  async list(): Promise<DrugsManifest> {
    const resp = await this.fetchFn(BASE_PATH, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(`DrugsServiceModule: list HTTP ${resp.status}`);
    }
    return (await resp.json()) as DrugsManifest;
  }

  async get(drugId: string): Promise<Record<string, unknown> | null> {
    const resp = await this.fetchFn(
      `${BASE_PATH}/${encodeURIComponent(drugId)}`,
      {
        method: 'GET',
        headers: { ...getAuthHeaders() },
      },
    );
    if (resp.status === 404) {
      return null;
    }
    if (!resp.ok) {
      throw new Error(`DrugsServiceModule: get HTTP ${resp.status}`);
    }
    return (await resp.json()) as Record<string, unknown>;
  }
}
